import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { 
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Loader2 } from "lucide-react";
import { ContactWithRelations } from "@/types/contact.types";

interface DeleteContactDialogProps {
  isOpen: boolean;
  onClose: () => void;
  contact: ContactWithRelations;
  onDelete: (id: string) => Promise<void>;
  redirectAfterDelete?: boolean;
}

export function DeleteContactDialog({
  isOpen,
  onClose,
  contact,
  onDelete,
  redirectAfterDelete = false,
}: DeleteContactDialogProps) {
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const contactFullName = `${contact.first_name} ${contact.last_name}`;

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      setError(null);
      await onDelete(contact.id);
      if (redirectAfterDelete) {
        navigate("/contacts");
      }
      onClose();
    } catch (err: any) {
      setError(err.message || "An error occurred while deleting the contact");
      setIsDeleting(false);
    }
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete {contactFullName}</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete this contact? This action cannot be undone.
            {error && (
              <div className="mt-2 rounded-md bg-destructive/10 p-3 text-destructive">
                {error}
              </div>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDelete}
            disabled={isDeleting}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isDeleting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Deleting...
              </>
            ) : (
              "Delete"
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}